import React, { useState, useEffect, useRef } from 'react';
import { getSessionHistory, getSessionImages, fetchAuthenticatedImage, deleteImage } from '../services/api';
import { API_BASE_URL } from '../config';
import JSZip from 'jszip';

function SessionHistory() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [images, setImages] = useState([]);
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState(false);
  const [downloadingSessionId, setDownloadingSessionId] = useState(null);
  const blobUrlsToCleanupRef = useRef([]);

  useEffect(() => {
    loadSessionHistory();
    
    // Cleanup blob URLs on unmount
    return () => {
      blobUrlsToCleanupRef.current.forEach(blobUrl => {
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
      });
    };
  }, []);

  const loadSessionHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSessionHistory();
      
      if (data.error) {
        setError(data.error);
        setSessions([]);
      } else {
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Error loading session history:', err);
      setError('Failed to load session history');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSessionImages = async (sessionId) => {
    // Toggle: if already viewing this session, close it
    if (selectedSession === sessionId && images.length > 0) {
      setSelectedSession(null);
      setImages([]);
      setImageBlobUrls({});
      return;
    }
    
    try {
      setLoadingImages(true);
      setSelectedSession(sessionId);
      setImages([]);
      setImageBlobUrls({});
      
      const data = await getSessionImages(sessionId);
      
      if (data.error) {
        console.error('Error loading images:', data.error);
        setImages([]);
        return;
      }
      
      const imageList = data.images || [];
      setImages(imageList);
      
      // Load all images as blob URLs
      for (const image of imageList) {
        try {
          const fullUrl = image.url.startsWith('http') ? image.url : `${API_BASE_URL}${image.url}`;
          const blobUrl = await fetchAuthenticatedImage(fullUrl);
          blobUrlsToCleanupRef.current.push(blobUrl);
          setImageBlobUrls(prev => ({ ...prev, [image.url]: blobUrl }));
        } catch (error) {
          console.error('Error loading image:', error);
        }
      }
    } catch (err) {
      console.error('Error loading session images:', err);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleDownloadSession = async (sessionId) => {
    try {
      setDownloadingSessionId(sessionId);
      
      // If this session isn't selected or doesn't have images loaded, load them first
      let imagesToDownload = images;
      let blobUrls = imageBlobUrls;
      
      if (selectedSession !== sessionId || images.length === 0) {
        const data = await getSessionImages(sessionId);
        if (data.error || !data.images || data.images.length === 0) {
          alert('No images found for this session');
          return;
        }
        
        imagesToDownload = data.images;
        blobUrls = {};
        
        // Load images as blobs
        for (const image of imagesToDownload) {
          try {
            const fullUrl = image.url.startsWith('http') ? image.url : `${API_BASE_URL}${image.url}`;
            const blobUrl = await fetchAuthenticatedImage(fullUrl);
            blobUrls[image.url] = blobUrl;
          } catch (error) {
            console.error('Error loading image for download:', error);
          }
        }
      }
      
      if (imagesToDownload.length === 0) {
        alert('No images to download');
        return;
      }
      
      const zip = new JSZip();
      
      // Add each image to the zip
      for (let i = 0; i < imagesToDownload.length; i++) {
        const image = imagesToDownload[i];
        const blobUrl = blobUrls[image.url];
        
        if (blobUrl) {
          try {
            const response = await fetch(blobUrl);
            const blob = await response.blob();
            zip.file(image.fileName, blob);
          } catch (error) {
            console.error(`Failed to add ${image.fileName} to zip:`, error);
          }
        }
      }
      
      // Generate and download the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `session-${sessionId}-photos.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 100);
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download images');
    } finally {
      setDownloadingSessionId(null);
    }
  };

  const handleDeleteImage = async (sessionId, image) => {
    if (!window.confirm(`Are you sure you want to delete this image?`)) {
      return;
    }

    try {
      const result = await deleteImage(sessionId, image.fileName);
      
      if (result.error) {
        alert('Failed to delete image: ' + result.error);
        return;
      }

      // Remove from images array
      setImages(prev => prev.filter(img => img.url !== image.url));
      
      // Clean up blob URL
      const blobUrl = imageBlobUrls[image.url];
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setImageBlobUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[image.url];
          return newUrls;
        });
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete image: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'ended':
        return '#6b7280'; // gray
      case 'active':
        return '#10b981'; // green
      case 'created':
        return '#3b82f6'; // blue
      default:
        return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading session history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
        <p>Error: {error}</p>
        <button className="btn btn-outline" onClick={loadSessionHistory} style={{ marginTop: '12px' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '1000px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '8px' }}>Session History</h2>
        <p className="hint" style={{ margin: 0 }}>
          View and download images from your previous sessions
        </p>
      </div>

      {sessions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <p>No sessions found</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {sessions.map((session) => (
            <div
              key={session.session_id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '16px',
                background: selectedSession === session.session_id ? '#f0f9ff' : '#fff',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, color: '#000' }}>Session {session.session_id}</h3>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#fff',
                        background: getStatusBadgeColor(session.status),
                      }}
                    >
                      {session.status}
                    </span>
                  </div>
                  {session.order_id && (
                    <p className="hint" style={{ margin: '0 0 4px 0' }}>
                      Order ID: {session.order_id}
                    </p>
                  )}
                  <p className="hint" style={{ margin: '0 0 4px 0' }}>
                    Created: {formatDate(session.created_at)}
                  </p>
                  {session.ended_at && (
                    <p className="hint" style={{ margin: 0 }}>
                      Ended: {formatDate(session.ended_at)}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-outline"
                    onClick={() => loadSessionImages(session.session_id)}
                    disabled={loadingImages && selectedSession === session.session_id}
                    style={{ padding: '6px 16px', fontSize: '14px' }}
                  >
                    {loadingImages && selectedSession === session.session_id
                      ? 'Loading...'
                      : selectedSession === session.session_id && images.length > 0
                      ? 'Hide Images'
                      : 'View Images'}
                  </button>
                  <button
                    className="btn"
                    onClick={() => handleDownloadSession(session.session_id)}
                    disabled={downloadingSessionId === session.session_id}
                    style={{ padding: '6px 16px', fontSize: '14px' }}
                  >
                    {downloadingSessionId === session.session_id ? 'Downloading...' : 'Download'}
                  </button>
                </div>
              </div>

              {/* Show images if this session is selected */}
              {selectedSession === session.session_id && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  {loadingImages ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p>Loading images...</p>
                    </div>
                  ) : images.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p>No images found for this session</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0 }}>Images ({images.length})</h4>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                          gap: '12px',
                        }}
                      >
                        {images.map((image, index) => {
                          const blobUrl = imageBlobUrls[image.url];
                          const hasGPS = image.latitude != null && image.longitude != null;
                          return (
                            <div key={index} style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
                              {blobUrl ? (
                                <>
                                  <a href={blobUrl} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={blobUrl}
                                      alt={image.fileName}
                                      style={{
                                        width: '100%',
                                        display: 'block',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                      }}
                                    />
                                  </a>
                                  <button
                                    onClick={() => handleDeleteImage(selectedSession, image)}
                                    style={{
                                      position: 'absolute',
                                      top: '4px',
                                      right: '4px',
                                      background: '#dc2626',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      padding: '4px 8px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: '600',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                    title="Delete image"
                                  >
                                    ×
                                  </button>
                                  {hasGPS && (
                                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ fontSize: '12px' }}>📍</span>
                                        <a
                                          href={`https://www.google.com/maps?q=${image.latitude},${image.longitude}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ 
                                            color: '#2563eb', 
                                            textDecoration: 'none',
                                            wordBreak: 'break-all'
                                          }}
                                          title={`${image.latitude.toFixed(6)}, ${image.longitude.toFixed(6)}`}
                                        >
                                          {image.latitude.toFixed(4)}, {image.longitude.toFixed(4)}
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    height: '140px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb',
                                    background: '#f3f4f6',
                                    color: '#6b7280',
                                  }}
                                >
                                  Loading...
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SessionHistory;

